import {
  Bento,
  Box,
  Button,
  Grid,
  Heading,
  Label,
  Link,
  River,
  RiverBreakout,
  SectionIntro,
  Text,
  Timeline,
} from '@primer/react-brand'
import {useEffect, useRef, type VideoHTMLAttributes} from 'react'
import {DeviceMobileIcon} from '@primer/octicons-react'
import {analyticsEvent} from '../../../../lib/analytics'
import {Image} from '../../../../components/Image/Image'

// Auto-play videos
//
// Starts playing videos when they're in view

interface VideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
  src: string
  poster: string
}

function useAutoplayVideo() {
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    const currentVideoRef = videoRef.current

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            // eslint-disable-next-line github/no-then
            if (currentVideoRef) currentVideoRef.play().catch(() => {})
          } else {
            if (currentVideoRef) currentVideoRef.pause()
          }
        }
      },
      {
        threshold: 0.5, // Play once 50% of the height is...
        rootMargin: `-25% 0% -25% 0%`, // ...in the center 50% of the viewport
      },
    )

    if (currentVideoRef) {
      observer.observe(currentVideoRef)
    }

    return () => {
      if (currentVideoRef) {
        observer.unobserve(currentVideoRef)
      }
    }
  }, [])

  return videoRef
}

function AutoPlayVideo({src, poster, ...props}: VideoProps) {
  const videoRef = useAutoplayVideo()

  return (
    <video ref={videoRef} playsInline muted preload="none" poster={poster} {...props}>
      <source src={src} type="video/mp4; codecs=avc1.4d002a" />
    </video>
  )
}

export default function FeaturesSection() {
  return (
    <section id="features" className="lp-Section lp-Section--noBottomPadding" style={{position: 'relative', zIndex: 1}}>
      <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
        <Grid.Column span={12}>
          <SectionIntro fullWidth align="center" className="lp-SectionIntro">
            <SectionIntro.Label size="large" className="lp-Label--section">
              Features
            </SectionIntro.Label>
            <SectionIntro.Heading size="2" weight="semibold">
              The AI coding assistant <br className="break-whenWide" /> elevating developer workflows
            </SectionIntro.Heading>
          </SectionIntro>
        </Grid.Column>

        <Grid.Column span={12}>
          <RiverBreakout style={{paddingTop: '0'}}>
            <a
              href="https://docs.github.com/en/copilot/github-copilot-chat/about-github-copilot-chat#use-cases-for-github-copilot-chat"
              aria-label="Accelerate workflow with interactive codebase chat"
              {...analyticsEvent({
                action: 'chat_docs',
                tag: 'video',
                context: 'copilot_chat',
                location: 'features',
              })}
            >
              <RiverBreakout.Visual aria-hidden="true" className="lp-River-visual">
                <AutoPlayVideo
                  src="/images/modules/site/copilot/features-breakout.mp4"
                  poster="/images/modules/site/copilot/features-breakout-poster.webp"
                  width="1248"
                  height="647"
                  className="d-none d-md-block hide-reduced-motion"
                  aria-label="Video demonstrating how GitHub Copilot accelerates workflow through interactive codebase chat"
                />
                <AutoPlayVideo
                  src="/images/modules/site/copilot/features-breakout-sm.mp4"
                  poster="/images/modules/site/copilot/features-breakout-poster-sm.webp"
                  width="350"
                  height="380"
                  className="d-block d-md-none hide-reduced-motion"
                  aria-label="Video demonstrating how GitHub Copilot accelerates workflow through interactive codebase chat"
                />
                <Image
                  src="/images/modules/site/copilot/features-breakout.webp"
                  alt="Screenshot showing how GitHub Copilot accelerates workflow through interactive codebase chat"
                  width="1248"
                  height="647"
                  style={{display: 'block'}}
                  className="hide-no-pref-motion"
                />
              </RiverBreakout.Visual>
            </a>
            <RiverBreakout.Content
              trailingComponent={() => (
                <Timeline className="lp-Timeline">
                  <Timeline.Item>
                    <em>Improve code quality and security.</em> Developers feel{' '}
                    <a
                      className="lp-Link--inline"
                      href="https://github.blog/2023-10-10-research-quantifying-github-copilots-impact-on-code-quality/"
                      {...analyticsEvent({
                        action: 'code_quality_blog',
                        tag: 'link',
                        context: 'copilot_chat',
                        location: 'features',
                      })}
                    >
                      more confident in their code quality
                    </a>{' '}
                    when authoring code with GitHub Copilot. And with the built-in{' '}
                    <a
                      className="lp-Link--inline"
                      href="https://github.blog/2023-02-14-github-copilot-now-has-a-better-ai-model-and-new-capabilities/#filtering-out-security-vulnerabilities-with-a-new-ai-system"
                      {...analyticsEvent({
                        action: 'vulnerability_blog',
                        tag: 'link',
                        context: 'copilot_chat',
                        location: 'features',
                      })}
                    >
                      vulnerability prevention system
                    </a>
                    , insecure coding patterns get blocked in real time.
                  </Timeline.Item>
                  <Timeline.Item>
                    <em>Enable greater collaboration.</em> GitHub Copilot’s the newest member of your team. You can ask
                    general programming questions or very specific ones about your codebase to get answers fast, learn
                    your way around, explain a mysterious regex, or get suggestions on how to improve legacy code.
                  </Timeline.Item>
                </Timeline>
              )}
            >
              <Text className="lp-RiverBreakout-text">
                <em>Start a conversation about your codebase.</em> Whether you’re hunting down a bug or designing a new
                feature, turn to GitHub Copilot when you’re stuck.
                <span style={{marginTop: '32px', marginBottom: '48px', display: 'block'}}>
                  <Link
                    href="https://docs.github.com/en/copilot/github-copilot-chat/about-github-copilot-chat#use-cases-for-github-copilot-chat"
                    variant="accent"
                    {...analyticsEvent({
                      action: 'chat_docs',
                      tag: 'link',
                      context: 'copilot_chat',
                      location: 'features',
                    })}
                  >
                    Read about use cases for GitHub Copilot Chat
                  </Link>
                </span>
              </Text>
            </RiverBreakout.Content>
          </RiverBreakout>

          <River imageTextRatio="60:40">
            <River.Visual aria-hidden="true" className="lp-River-visual">
              <a
                href="https://docs.github.com/en/copilot/quickstart#introduction"
                tabIndex={-1}
                aria-hidden="true"
                {...analyticsEvent({
                  action: 'quickstart_docs',
                  tag: 'video',
                  context: 'ai_suggestions',
                  location: 'features',
                })}
              >
                <AutoPlayVideo
                  src="/images/modules/site/copilot/features-river-1.mp4"
                  poster="/images/modules/site/copilot/features-river-1-poster.webp"
                  width="708"
                  height="472"
                  className="d-none d-md-block hide-reduced-motion"
                  aria-label="Video showing editor with GitHub Copilot code suggestions in real time"
                />
                <AutoPlayVideo
                  src="/images/modules/site/copilot/features-river-1-sm.mp4"
                  poster="/images/modules/site/copilot/features-river-1-poster-sm.webp"
                  width="350"
                  height="290"
                  className="d-block d-md-none hide-reduced-motion"
                  aria-label="Video showing editor with GitHub Copilot code suggestions in real time"
                />
                <Image
                  src="/images/modules/site/copilot/features-river-1.webp"
                  alt="Editor with GitHub Copilot code suggestions in real time"
                  width="708"
                  height="472"
                  className="hide-no-pref-motion"
                />
              </a>
            </River.Visual>
            <River.Content>
              <Heading as="h3" size="5">
                Get AI-based suggestions in real time
              </Heading>
              <Text as="p" variant="muted" className="lp-River-text">
                GitHub Copilot suggests code completions as developers type and turns natural language prompts into
                coding suggestions based on the project&apos;s context and style conventions.
              </Text>
              <Link
                href="https://docs.github.com/en/copilot/quickstart#introduction"
                variant="accent"
                {...analyticsEvent({
                  action: 'quickstart_docs',
                  tag: 'link',
                  context: 'ai_suggestions',
                  location: 'features',
                })}
              >
                Read the docs
              </Link>
            </River.Content>
          </River>

          <River imageTextRatio="60:40">
            <River.Visual aria-hidden="true" className="lp-River-visual">
              <a
                href="https://docs.github.com/copilot/customizing-copilot/adding-custom-instructions-for-github-copilot"
                tabIndex={-1}
                aria-hidden="true"
                {...analyticsEvent({
                  action: 'docset_docs',
                  tag: 'video',
                  context: 'documentation',
                  location: 'features',
                })}
              >
                <Image
                  src="/images/modules/site/copilot/features-river-2-alt.webp?v=2"
                  alt="GitHub Copilot running custom instruction"
                  width="708"
                  height="472"
                />
              </a>
            </River.Visual>
            <River.Content>
              <Heading as="h3" size="5">
                <Label className="lp-Label--inRiver" style={{lineHeight: 'var(--base-size-24)'}}>
                  Technical&nbsp;Preview
                </Label>
                Tailor-made answers, defined by you
              </Heading>
              <Text as="p" variant="muted" className="lp-River-text">
                Specify custom instructions to personalize chat responses in VS Code and Visual Studio based on your
                preferred tools, organizational knowledge, and coding best practices.
              </Text>
              <Link
                href="https://docs.github.com/copilot/customizing-copilot/adding-custom-instructions-for-github-copilot"
                variant="accent"
                {...analyticsEvent({
                  action: 'docs_instructions',
                  tag: 'link',
                  context: 'documentation',
                  location: 'features',
                })}
              >
                Create custom instructions
              </Link>
            </River.Content>
          </River>

          <River imageTextRatio="60:40">
            <River.Visual aria-hidden="true" className="lp-River-visual">
              <a
                href="https://github.com/features/preview/copilot-code-review"
                tabIndex={-1}
                aria-hidden="true"
                {...analyticsEvent({
                  action: 'pr_review',
                  tag: 'video',
                  context: 'documentation',
                  location: 'features',
                })}
              >
                <Image
                  src="/images/modules/site/copilot/features-river-3-alt.webp?v=2"
                  alt="GitHub Copilot adding a review to a pull request"
                  width="708"
                  height="472"
                />
              </a>
            </River.Visual>
            <River.Content>
              <Heading as="h3" size="5">
                <Label className="lp-Label--inRiver" style={{lineHeight: 'var(--base-size-24)'}}>
                  Technical&nbsp;Preview
                </Label>
                Feedback without the wait
              </Heading>
              <Text as="p" variant="muted" className="lp-River-text">
                Start iterating and moving towards “ready to merge” instantly. As your first stop for a code review,
                Copilot will spot hidden bugs, tidy up spelling and grammar mistakes, level-up your error handling, and
                more – all while you wait for a human review.
              </Text>
              <Link
                href="https://github.com/features/preview/copilot-code-review"
                variant="accent"
                {...analyticsEvent({
                  action: 'pr_docs',
                  tag: 'link',
                  context: 'pull_requests',
                  location: 'features',
                })}
              >
                Get early access to code review
              </Link>
            </River.Content>
          </River>

          <River imageTextRatio="60:40" style={{paddingBottom: '0'}}>
            <River.Visual aria-hidden="true" className="lp-River-visual">
              <a href="https://github.com/features/copilot/extensions" tabIndex={-1} aria-hidden="true">
                <AutoPlayVideo
                  src="/images/modules/site/copilot/features-river-4.mp4"
                  poster="/images/modules/site/copilot/features-river-4-poster.webp"
                  width="708"
                  height="472"
                  className="d-none d-md-block hide-reduced-motion"
                  aria-label="Video showing GitHub Copilot chat with a list of extensions"
                />
                <AutoPlayVideo
                  src="/images/modules/site/copilot/features-river-4-sm.mp4"
                  poster="/images/modules/site/copilot/features-river-4-poster-sm.webp"
                  width="350"
                  height="290"
                  className="d-block d-md-none hide-reduced-motion"
                  aria-label="Video showing GitHub Copilot chat with a list of extensions"
                />
                <Image
                  src="/images/modules/site/copilot/features-river-4.webp"
                  alt="GitHub Copilot chat showing a list of extensions"
                  width="708"
                  height="472"
                  loading="lazy"
                  className="hide-no-pref-motion"
                />
              </a>
            </River.Visual>
            <River.Content>
              <Heading as="h3" size="5">
                <Label className="lp-Label--inRiver" style={{lineHeight: 'var(--base-size-24)'}}>
                  Technical&nbsp;Preview
                </Label>
                Your favorite tools have entered the chat
              </Heading>
              <Text as="p" variant="muted" className="lp-River-text">
                Check log errors, create feature flags, deploy apps to the cloud. Add capabilities to GitHub Copilot
                with an ecosystem of extensions from third-party tools and services.
              </Text>
              <Link
                href="https://github.com/features/copilot/extensions"
                variant="accent"
                {...analyticsEvent({action: 'extensions', tag: 'link', context: 'tools', location: 'features'})}
              >
                Explore GitHub Copilot Extensions
              </Link>
            </River.Content>
          </River>
        </Grid.Column>

        {/* Spacer */}
        <Box paddingBlockStart={{narrow: 96, regular: 128}} aria-hidden />

        <Grid.Column span={12}>
          <Bento className="Bento Bento--raised">
            <Bento.Item
              columnSpan={12}
              rowSpan={5}
              flow={{
                xsmall: 'row',
                medium: 'column',
              }}
              colorMode="dark"
              className="lp-Features-bento-5"
              style={{gridGap: 0}}
            >
              <Bento.Content
                padding={{
                  xsmall: 'normal',
                  medium: 'spacious',
                }}
              >
                <Bento.Heading as="h3" size="4" weight="semibold">
                  <Label className="lp-Label--inBento">Limited Public Beta</Label>
                  Need a custom solution? <span style={{whiteSpace: 'nowrap'}}>Fine-tune</span> a private model for code
                  suggestions tailored to your practices
                </Bento.Heading>
                <Link
                  href="https://github.com/github-copilot/fine_tuning_waitlist_signup/join"
                  size="large"
                  variant="default"
                  {...analyticsEvent({
                    action: 'copilot_models_waitlist',
                    tag: 'link',
                    context: 'models',
                    location: 'features',
                  })}
                >
                  Join the waitlist
                </Link>
              </Bento.Content>
              <Bento.Visual position="16px 0" fillMedia className="lp-Features-bento-5-visual">
                <Image
                  src="/images/modules/site/copilot/features-bento-5-visual.webp"
                  alt="Training history of a fine-tuned model for GitHub Copilot"
                />
              </Bento.Visual>
            </Bento.Item>

            <Bento.Item
              columnSpan={{xsmall: 12, medium: 7}}
              rowSpan={{xsmall: 5, medium: 5, large: 5, xlarge: 6}}
              visualAsBackground
            >
              <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}} verticalAlign="start">
                <Bento.Heading as="h3" size="4" weight="semibold">
                  Ask for assistance right in your terminal
                </Bento.Heading>
                <Link
                  href="https://docs.github.com/copilot/github-copilot-in-the-cli"
                  size="large"
                  variant="default"
                  {...analyticsEvent({action: 'cli_docs', tag: 'link', context: 'terminal', location: 'features'})}
                >
                  Try Copilot in the CLI
                </Link>
              </Bento.Content>
              <Bento.Visual position="0% 50%">
                <Image
                  src="/images/modules/site/copilot/features-bento-1-cli-ga.webp"
                  alt="Screenshot of GitHub Copilot CLI in a terminal"
                  width="724"
                  height="620"
                  className="lp-Features-bento-1-visual object-pos-left-bottom"
                />
              </Bento.Visual>
            </Bento.Item>

            <Bento.Item
              columnSpan={{xsmall: 12, medium: 5}}
              rowSpan={{xsmall: 5, medium: 5, large: 5, xlarge: 6}}
              className="Bento-item"
            >
              <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}} horizontalAlign="center">
                <Bento.Heading as="h3" size="4" weight="semibold">
                  Keep flying with your favorite editor
                </Bento.Heading>
              </Bento.Content>
              <Bento.Visual
                padding={{xsmall: 'normal', xlarge: 'spacious'}}
                fillMedia={false}
                className="lp-Features-editorContainer"
              >
                <Button
                  as="a"
                  href="https://marketplace.visualstudio.com/items?itemName=GitHub.copilot"
                  hasArrow={false}
                  className="lp-Features-editorButton"
                  {...analyticsEvent({action: 'vscode', tag: 'icon', context: 'editors', location: 'features'})}
                >
                  <Image src="/images/modules/site/copilot/features-bento-2-vscode.svg" alt="" width="90" height="90" />
                  <Text as="div" size="200" weight="normal">
                    VS Code
                  </Text>
                </Button>

                <Button
                  as="a"
                  href="https://marketplace.visualstudio.com/items?itemName=GitHub.copilotvs"
                  hasArrow={false}
                  className="lp-Features-editorButton"
                  {...analyticsEvent({action: 'vs', tag: 'icon', context: 'editors', location: 'features'})}
                >
                  <Image
                    src="/images/modules/site/copilot/features-bento-2-visualstudio.svg"
                    alt=""
                    width="90"
                    height="90"
                  />
                  <Text as="div" size="200" weight="normal">
                    Visual Studio
                  </Text>
                </Button>

                <Button
                  as="a"
                  href="https://plugins.jetbrains.com/plugin/17718-github-copilot"
                  hasArrow={false}
                  className="lp-Features-editorButton"
                  {...analyticsEvent({action: 'jetbrains', tag: 'icon', context: 'editors', location: 'features'})}
                >
                  <Image
                    src="/images/modules/site/copilot/features-bento-2-jetbrains.svg"
                    alt=""
                    width="90"
                    height="90"
                  />
                  <Text as="div" size="200" weight="normal">
                    JetBrains IDEs
                  </Text>
                </Button>
                <Button
                  as="a"
                  href="https://github.com/github/CopilotForXcode"
                  hasArrow={false}
                  className="lp-Features-editorButton"
                  {...analyticsEvent({action: 'xcode', tag: 'icon', context: 'editors', location: 'features'})}
                >
                  <Image src="/images/modules/site/copilot/features-bento-2-xcode.svg" alt="" width="90" height="90" />
                  <Text as="div" size="200" weight="normal">
                    Xcode
                  </Text>
                </Button>
              </Bento.Visual>
            </Bento.Item>

            <Bento.Item
              columnSpan={{xsmall: 12, medium: 5, large: 5, xlarge: 5}}
              rowSpan={{xsmall: 4, small: 4, medium: 4, xlarge: 5}}
              className="Bento-item"
            >
              <Bento.Content
                horizontalAlign={{xsmall: 'center', large: 'start'}}
                padding={{xsmall: 'normal', xlarge: 'spacious'}}
                leadingVisual={<DeviceMobileIcon />}
                className="lp-Features-mobile"
              >
                <Bento.Heading as="h3" size="4" weight="semibold" className="lp-Features-mobileText">
                  Chat with your AI pair programmer on-the-go
                </Bento.Heading>
              </Bento.Content>
              <Bento.Visual
                padding={{xsmall: 'normal', xlarge: 'spacious'}}
                fillMedia={false}
                horizontalAlign="center"
                verticalAlign="end"
                style={{columnGap: '24px', flexWrap: 'wrap'}}
              >
                <a
                  href="https://play.google.com/store/apps/details?id=com.github.android"
                  {...analyticsEvent({
                    action: 'play_store',
                    tag: 'button',
                    context: 'mobile_apps',
                    location: 'features',
                  })}
                >
                  <Image
                    src="/images/modules/site/copilot/features-bento-4-google.png"
                    alt="Google Play Store logo"
                    width="180"
                    height="53"
                    style={{display: 'block'}}
                  />
                </a>
                <a
                  href="https://apps.apple.com/app/github/id1477376905?ls=1"
                  {...analyticsEvent({
                    action: 'app_store',
                    tag: 'button',
                    context: 'mobile_apps',
                    location: 'features',
                  })}
                >
                  <Image
                    src="/images/modules/site/copilot/features-bento-4-apple.png"
                    alt="Apple App Store logo"
                    width="179"
                    height="53"
                    style={{display: 'block'}}
                  />
                </a>
              </Bento.Visual>
            </Bento.Item>

            <Bento.Item
              columnSpan={{xsmall: 12, medium: 7, large: 7, xlarge: 7}}
              rowSpan={{xsmall: 4, small: 3, medium: 4, xlarge: 5}}
            >
              <Bento.Visual position="25% 0%">
                <Image
                  src="/images/modules/site/copilot/features-bento-3-chat-ga.webp"
                  alt="A phone showing GitHub Copilot in GitHub Mobile"
                  width="724"
                  height="560"
                />
              </Bento.Visual>
            </Bento.Item>
          </Bento>
        </Grid.Column>
      </Grid>
    </section>
  )
}

try{ AutoPlayVideo.displayName ||= 'AutoPlayVideo' } catch {}
try{ FeaturesSection.displayName ||= 'FeaturesSection' } catch {}